import { type ButtonProps } from '@components/Button.tsx';
import { CancelSubmitButtons } from './CancelSubmitButtons';
import { Icon } from '@components/Icon.tsx';
import { IconType } from '@src/IconType.ts';
import {
  type ReactElement,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { classNames } from '@utils/classNames.ts';

export type InlineEditProps = {
  onSubmit: () => Promise<void> | void;
  onCancel?: () => void;
  className?: string;
  classNameEditingContainer?: string;
  ValueComponent:
    | ReactElement
    | string
    | number
    | boolean
    | Iterable<ReactNode>
    | Promise<Awaited<ReactNode>>
    | bigint;
  EditComponent: ReactElement;
  testId?: string;
  showButtons?: boolean;
  passthrough?: { submitButton?: Partial<ButtonProps> };
  size?: 'small' | 'medium';
  isEditing?: boolean;
  setIsEditing?: (isEditing: boolean) => void;
  disabled?: boolean;
};

export function InlineEdit({
  isEditing: isEditingProp,
  setIsEditing: setIsEditingProp,
  onSubmit,
  onCancel,
  className,
  classNameEditingContainer,
  ValueComponent,
  EditComponent,
  testId,
  showButtons = true,
  passthrough,
  size = 'medium',
  disabled,
}: InlineEditProps) {
  const [isEditing, setIsEditing] = useState(false);
  useEffect(() => {
    if (isEditingProp !== undefined) {
      setIsEditing(isEditingProp);
    }
  }, [isEditingProp]);
  const startEditing = useCallback(() => {
    if (disabled) {
      return;
    }
    setIsEditing(true);
    setIsEditingProp?.(true);
  }, [setIsEditingProp, disabled]);

  const cancelEditing = useCallback(() => {
    setIsEditing(false);
    setIsEditingProp?.(false);
    onCancel?.();
  }, [onCancel, setIsEditingProp]);

  const submit = useCallback(async () => {
    await onSubmit();
    setIsEditing(false);
    setIsEditingProp?.(false);
  }, [onSubmit, setIsEditingProp]);

  const sizeClasses = useMemo(() => {
    switch (size) {
      case 'small':
        return { button: 'p-0', icon: 'size-5' };
      case 'medium':
        return { button: '-ml-1 p-1', icon: 'size-6' };
    }
  }, [size]);

  return isEditing ? (
    <div
      className={classNames(
        'group hover:bg-gray-100 p-1 rounded flex gap-2.5',
        showButtons
          ? 'flex-col justify-end w-full'
          : 'items-center justify-stretch',
        classNameEditingContainer,
      )}
    >
      {EditComponent}
      {showButtons ? (
        <CancelSubmitButtons
          onCancel={cancelEditing}
          onSubmit={submit}
          passthrough={passthrough}
        />
      ) : (
        <div className="hidden group-hover:block">
          <Icon
            type={IconType.Pencil}
            className="w-6 h-6 text-slate-900 shrink-0"
          />
        </div>
      )}
    </div>
  ) : (
    <button
      className={classNames(
        'group flex justify-end sm:justify-between items-center gap-2.5 text-left rounded',
        !disabled && 'hover:bg-gray-100 cursor-pointer',
        sizeClasses.button,
        className,
      )}
      onClick={startEditing}
      data-testid={testId}
      disabled={disabled}
    >
      {ValueComponent}
      {!disabled && (
        <div
          className={classNames(
            'block md:hidden group-hover:block',
            showButtons &&
              'bg-white group-hover:border-blue-50 border-transparent border rounded -my-0.5',
          )}
        >
          <Icon
            type={IconType.Pencil}
            className={classNames('text-slate-900 shrink-0', sizeClasses.icon)}
          />
        </div>
      )}
    </button>
  );
}
